import { css } from '@inter-ikea-kompis/web-component';
import { KeyboardTypeEnum } from '@inter-ikea-kompis/keyboard-manager';
import type { ITheme } from '@inter-ikea-kompis/types';

import KeyboardKeyCodeEnum from '../../enums/KeyboardKeyCodeEnum.js';

export default class KeyboardButtonStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @param options.keyCode KeyboardKeyCodeEnum, the allowed buttons that exists on the keyboard.
	 * @param options.type KeyboardTypeEnum, for instance text or measurement keyboards.
	 * @param options.isActive "true" if mousedown is active for the button.
	 * @returns CSS.
	 */
	public static getStyle(options: {
		theme: ITheme;
		keyCode: KeyboardKeyCodeEnum | null;
		type: KeyboardTypeEnum;
		isActive: boolean;
	}): string {
		const { color, spacing } = options.theme;

		return css`
			:host {
				display: inline-block;
			}

			button {
				display: inline-flex;
				justify-content: center;
				align-items: center;
				width: ${this.getWidth(options)};
				height: ${(90 / 1920) * 100}vw;
				padding: 0;
				border: ${1 / 16}rem solid ${color.neutral.grey900};
				border-radius: ${4 / 16}rem;
				margin-right: ${(spacing.space100 / 1920) * 100}vw;
				background: ${this.getBackgroundColor(options)};
				outline: none;
				box-sizing: border-box;
				font-family: ${this.getFontFamily(options.theme)};
				font-size: ${(50 / 1920) * 100}vw;
			}
		`;
	}

	private static getBackgroundColor(options: {
		theme: ITheme;
		keyCode: string | null;
		isActive: boolean;
	}): string {
		const { color } = options.theme;

		if (options.isActive) {
			return `${color.neutral.grey300};`;
		}

		if (
			options.keyCode === KeyboardKeyCodeEnum.backspace ||
			options.keyCode === KeyboardKeyCodeEnum.space
		) {
			return `${color.neutral.grey100};`;
		}

		return `${color.neutral.white}`;
	}

	// Returns keyboard button size.
	private static getWidth(options: {
		keyCode: KeyboardKeyCodeEnum | null;
		type: KeyboardTypeEnum;
	}): string {
		if (this.isLargeKey(options)) {
			return `${(196 / 1920) * 100}vw`;
		}

		return `${(90 / 1920) * 100}vw`;
	}

	// Returns true if key is supposed to be larger than the default size for the current keyboard type.
	private static isLargeKey(options: {
		keyCode: KeyboardKeyCodeEnum | null;
		type: KeyboardTypeEnum;
	}): boolean {
		switch (options.type) {
			case KeyboardTypeEnum.number:
				return options.keyCode === KeyboardKeyCodeEnum.number0;
			case KeyboardTypeEnum.text:
			case KeyboardTypeEnum.email:
			case KeyboardTypeEnum.measurement:
				return (
					options.keyCode === KeyboardKeyCodeEnum.backspace ||
					options.keyCode === KeyboardKeyCodeEnum.space
				);
		}

		return false;
	}

	private static getFontFamily(theme: ITheme): string {
		return [theme.fontFace.family, ...theme.fontFace.fallbacks]
			.map((font) => `'${font}'`)
			.join(',');
	}
}
